import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, PlanetName } from '../../types/kundali';
import {
  VartamanaHorizon,
  LifeAreaThemeId,
  EvidenceStrength,
  ActivationLifecycleState,
  VartamanaAnubhavaSignature
} from '../../types/vartamanaSthiti';
import {
  calculateVartamanaCurrentState,
  calculateHorizonTemporalEvolution
} from '../../utils/vartamanaSthitiEngine';
import { formatDate } from '../../utils/dateUtils';
import WhatsOnYourMindSection from './WhatsOnYourMindSection';
import {
  Compass,
  Clock,
  Calendar,
  Layers,
  Sparkles,
  Shield,
  Activity,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  TrendingUp,
  Flame,
  ArrowRight,
  Split,
  FileText,
  BookOpen,
  Award,
  Zap,
  Info,
  CheckCircle2,
  Heart,
  Briefcase,
  Home,
  UserCheck,
  Moon as MoonIcon,
  HelpCircle,
  Clock3,
  Sun
} from 'lucide-react';

interface VartamanaSthitiViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onOpenConsultation?: (prompt?: string) => void;
}

export const VartamanaSthitiView: React.FC<VartamanaSthitiViewProps> = ({
  kundali,
  lang = 'en',
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  // 1. Independent Interactive State: Focus Date vs. Time Horizon
  const [focusDateStr, setFocusDateStr] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [selectedHorizon, setSelectedHorizon] = useState<VartamanaHorizon>('current_ad');

  // Presentation & UI State
  const [isAcharyaView, setIsAcharyaView] = useState<boolean>(false);
  const [showCompareWithToday, setShowCompareWithToday] = useState<boolean>(false);
  const [expandedThemeId, setExpandedThemeId] = useState<LifeAreaThemeId | null>(null);
  const [expandedAnubhavaId, setExpandedAnubhavaId] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<'ALL' | 'ACTIVE' | 'PEAK' | 'EMERGING' | 'RELEASING'>('ALL');
  const [activeAcharyaTab, setActiveAcharyaTab] = useState<'D1_VARGA' | 'DASHA_SANDHI' | 'GOCHAR_TRIGGERS' | 'REASONING_CHAIN'>('D1_VARGA');

  // ==========================================================================
  // 2. ARCHITECTURAL SEPARATION:
  // currentState is bound strictly to focusDateStr (and kundali / lang)
  // Changing selectedHorizon NEVER invalidates or recalculates currentState!
  // ==========================================================================
  const currentState = useMemo(() => {
    return calculateVartamanaCurrentState(kundali, new Date(focusDateStr), lang);
  }, [kundali, focusDateStr, lang]);

  // horizonEvolution is recalculated when selectedHorizon or currentState changes
  const horizonEvolution = useMemo(() => {
    return calculateHorizonTemporalEvolution(kundali, currentState, selectedHorizon, new Date(focusDateStr), lang);
  }, [kundali, currentState, selectedHorizon, focusDateStr, lang]);

  const {
    validityWindow,
    snapshot,
    dasha5Level,
    gocharTriggers,
    activeThemes,
    emergingThemes,
    peakingThemes,
    releasingThemes,
    dormantThemes,
    currentLivedExperiences,
    convergenceSignatures,
    hasLivedExperiences,
    noSignatureNoticeEn,
    noSignatureNoticeHi,
    btrStatus,
    acharyaGatewayStatus
  } = currentState;

  const {
    horizonAnalysis,
    timelineMarkers,
    comparisonWithToday
  } = horizonEvolution;

  // Quick preset dates relative to today
  const handleQuickPreset = (offsetDays: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    setFocusDateStr(d.toISOString().split('T')[0]);
  };

  const getLifecycleBadgeClass = (state: ActivationLifecycleState) => {
    switch (state) {
      case 'Peak':
        return 'bg-amber-100 text-amber-950 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-700 font-extrabold';
      case 'Intensifying':
      case 'Active':
        return 'bg-emerald-100 text-emerald-950 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-700 font-bold';
      case 'Emerging':
        return 'bg-indigo-100 text-indigo-950 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-200 dark:border-indigo-700 font-semibold';
      case 'Releasing':
      case 'Fading':
        return 'bg-astro-secondary-surface text-astro-secondary border-astro-border font-medium';
      default:
        return 'bg-astro-card text-astro-secondary-text border-astro-border font-normal';
    }
  };

  const getEvidenceStrengthBadge = (strength: EvidenceStrength) => {
    switch (strength) {
      case 'Strongly Corroborated':
        return 'bg-emerald-600 text-white font-bold';
      case 'Strong':
        return 'bg-teal-600 text-white font-semibold';
      case 'Mixed / Conflicted':
        return 'bg-amber-600 text-white font-bold';
      case 'Moderate':
        return 'bg-sky-700 text-white font-medium';
      default:
        return 'bg-astro-secondary text-white font-normal';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'MANAS_CHITTA':
        return <MoonIcon className="w-4 h-4 text-indigo-500" />;
      case 'KARMA_CAREER':
        return <Briefcase className="w-4 h-4 text-blue-600" />;
      case 'GRIHA_RESIDENCE':
        return <Home className="w-4 h-4 text-emerald-600" />;
      case 'SAMBANDHA':
        return <Heart className="w-4 h-4 text-rose-500" />;
      case 'CONVERGENCE':
        return <Flame className="w-4 h-4 text-amber-600" />;
      default:
        return <Compass className="w-4 h-4 text-astro-accent" />;
    }
  };

  const displayedThemes = useMemo(() => {
    if (selectedFilter === 'PEAK') return peakingThemes;
    if (selectedFilter === 'ACTIVE') return activeThemes.filter(t => t.lifecycleState !== 'Peak');
    if (selectedFilter === 'EMERGING') return emergingThemes;
    if (selectedFilter === 'RELEASING') return releasingThemes;
    return [...activeThemes, ...emergingThemes, ...releasingThemes];
  }, [selectedFilter, activeThemes, emergingThemes, peakingThemes, releasingThemes]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12 text-astro-text">
      {/* =================================================================== */}
      {/* 1. TOP HEADER & FOCUS DATE CONTROLLER (ANCHORS CURRENT STATE)        */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-astro-border/70 pb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-astro-primary text-white text-xs font-bold uppercase tracking-wider">
                <Compass className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'वर्तमान स्थिति' : 'Vartamāna Sthiti'}
              </span>
              <span className="text-xs px-2.5 py-0.5 rounded-md bg-white dark:bg-astro-bg text-astro-primary border border-astro-border font-bold">
                {isHi ? 'शीर्ष-स्तरीय वर्तमान जीवन अवस्था' : 'Top-Level Current Life State'}
              </span>
              {dasha5Level.isDashaSandhiActive && (
                <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 border border-amber-300 font-bold flex items-center gap-1 animate-pulse">
                  <AlertTriangle className="w-3 h-3" />
                  {isHi ? 'दशा संधि सक्रिय' : 'Dasha Sandhi Transition'}
                </span>
              )}
            </div>

            <h1 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary mt-2">
              {isHi ? 'वर्तमान जीवन अवस्था (Current Life State)' : 'Vartamāna Sthiti: What is Astrologically Active Right Now?'}
            </h1>
            <p className="text-xs text-astro-secondary-text mt-1 max-w-3xl leading-relaxed">
              {isHi
                ? 'यह खंड चयनित केंद्रित तिथि पर आपकी वास्तविक ज्योतिषीय अवस्था को प्रस्तुत करता है। स्थिति (ग्रहीय विन्यास), अर्थ (शास्त्रीय तात्पर्य) एवं फल (संभावित अभिव्यक्ति) को स्पष्ट रूप से पृथक रखा गया है।'
                : 'Deterministic Jyotish state identifying active life themes, immediate lived experiences (Vartamāna Anubhava), and trigger windows strictly anchored to your Focus Date. Strictly distinguishes mechanical Sthiti, classical Artha, and experiential Phala.'}
            </p>
          </div>

          {/* Presentation Depth Switcher & Compare with Today */}
          <div className="flex items-center gap-2 self-start lg:self-center shrink-0">
            <button
              type="button"
              onClick={() => setIsAcharyaView(!isAcharyaView)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer ${
                isAcharyaView
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-white dark:bg-astro-bg text-astro-secondary-text border-astro-border hover:text-astro-primary'
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              <span>{isAcharyaView ? (isHi ? 'आचार्य दृश्य (सक्रिय)' : 'Acharya Deep Dive (Active)') : (isHi ? 'जिज्ञासु दृश्य (सरल)' : 'Seeker View (Humanized)')}</span>
            </button>

            <button
              type="button"
              onClick={() => setShowCompareWithToday(!showCompareWithToday)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer ${
                showCompareWithToday
                  ? 'bg-astro-accent text-white border-astro-accent shadow-xs'
                  : 'bg-white dark:bg-astro-bg text-astro-secondary-text border-astro-border hover:text-astro-accent'
              }`}
            >
              <Split className="w-3.5 h-3.5" />
              <span>{isHi ? 'आज से तुलना' : 'Compare with Today'}</span>
            </button>
          </div>
        </div>

        {/* Focus Date Controller (Anchors the Current State Engine) */}
        <div className="pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-astro-primary text-[11px] uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'सक्रिय केंद्रित तिथि (Active Focus Date):' : 'Active Focus Date & Reference Point:'}
              </span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-astro-bg border border-astro-border text-astro-secondary-text">
                {currentState.timezone}
              </span>
            </div>
            <p className="text-[10px] text-astro-secondary-text">
              {isHi
                ? 'तिथि बदलने पर ५-स्तरीय दशा, तात्कालिक गोचर, वर्तमान अनुभूति एवं वैधता कालखंड पुनः परिकलित होते हैं।'
                : 'Changing Focus Date recalculates the entire 5-tier Dasha, active Gochar triggers, and Current State Validity Window.'}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <input
              type="date"
              value={focusDateStr}
              onChange={(e) => setFocusDateStr(e.target.value)}
              className="bg-white dark:bg-astro-bg border border-astro-border rounded-lg px-2.5 py-1 text-xs font-mono font-semibold text-astro-text focus:outline-none focus:ring-1 focus:ring-astro-primary"
            />

            <div className="flex items-center gap-1 text-[10px]">
              <button
                type="button"
                onClick={() => handleQuickPreset(0)}
                className="px-2 py-1 rounded bg-white dark:bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-primary font-bold cursor-pointer"
              >
                {isHi ? 'आज' : 'Today'}
              </button>
              <button
                type="button"
                onClick={() => handleQuickPreset(7)}
                className="px-2 py-1 rounded bg-white dark:bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-primary font-medium cursor-pointer"
              >
                +7D
              </button>
              <button
                type="button"
                onClick={() => handleQuickPreset(30)}
                className="px-2 py-1 rounded bg-white dark:bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-primary font-medium cursor-pointer"
              >
                +30D
              </button>
              <button
                type="button"
                onClick={() => handleQuickPreset(90)}
                className="px-2 py-1 rounded bg-white dark:bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-primary font-medium cursor-pointer"
              >
                +90D
              </button>
              <button
                type="button"
                onClick={() => handleQuickPreset(365)}
                className="px-2 py-1 rounded bg-white dark:bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-primary font-medium cursor-pointer"
              >
                +1Y
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 2. COMPARE WITH TODAY DRAWER (ACCORDION)                            */}
      {/* =================================================================== */}
      {showCompareWithToday && (
        <div className="bg-astro-accent/5 border border-astro-accent/30 rounded-2xl p-4.5 space-y-3 animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-astro-accent/20 pb-2">
            <div className="flex items-center gap-2">
              <Split className="w-4 h-4 text-astro-accent" />
              <h3 className="font-bold text-astro-text text-sm font-serif">
                {isHi ? `आज (${formatDate(new Date())}) बनाम केंद्रित तिथि (${currentState.focusDateFormatted})` : `Comparison: Today vs. Selected Focus Date (${currentState.focusDateFormatted})`}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setShowCompareWithToday(false)}
              className="text-xs text-astro-secondary-text hover:text-astro-text font-bold cursor-pointer"
            >
              ✕ {isHi ? 'बंद करें' : 'Close'}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1">
              <span className="text-[10px] text-astro-secondary-text font-semibold uppercase block">
                {isHi ? 'दशा पदानुक्रम परिवर्तन' : 'Dasha Hierarchy Shift'}
              </span>
              <p className="font-bold text-astro-primary text-xs">
                {comparisonWithToday.dashaDiff.focusDasha}
              </p>
              {comparisonWithToday.dashaDiff.hasLevelShift ? (
                <span className="text-[10px] text-amber-700 dark:text-amber-300 font-medium block">
                  ★ Shifted: {comparisonWithToday.dashaDiff.shiftedLevels.join(', ')}
                </span>
              ) : (
                <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium block">
                  ✓ Same continuous Dasha period
                </span>
              )}
            </div>

            <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1">
              <span className="text-[10px] text-astro-secondary-text font-semibold uppercase block">
                {isHi ? 'नव-सक्रिय जीवन क्षेत्र' : 'Newly Active Themes'}
              </span>
              {comparisonWithToday.themeDiff.newlyEmergingThemes.length > 0 ? (
                <ul className="text-xs space-y-0.5 font-semibold text-emerald-800 dark:text-emerald-300">
                  {comparisonWithToday.themeDiff.newlyEmergingThemes.map((nt, idx) => (
                    <li key={idx}>+ {nt}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-astro-secondary-text italic">
                  {isHi ? 'मुख्य जीवन क्षेत्रों में निरंतरता' : 'No new theme shifts on this date.'}
                </p>
              )}
            </div>

            <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1">
              <span className="text-[10px] text-astro-secondary-text font-semibold uppercase block">
                {isHi ? 'गोचर में मुख्य अंतर' : 'Gochar Trigger Delta'}
              </span>
              {comparisonWithToday.gocharDiff.exactTriggersDifference.length > 0 ? (
                <ul className="text-[11px] space-y-0.5 text-astro-text font-medium">
                  {comparisonWithToday.gocharDiff.exactTriggersDifference.slice(0, 2).map((gt, idx) => (
                    <li key={idx}>• {gt}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-astro-secondary-text italic">
                  {isHi ? 'सामान्य गोचर गति' : 'Planetary orbits advancing predictably.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 3. VARTAMĀNA STHITI — CURRENT LIFE STATE (TOP-LEVEL SECTION)        */}
      {/* Mandatory: Must appear ABOVE Time Horizon and remain independent    */}
      {/* =================================================================== */}
      <div className="space-y-4">
        {/* Section Title Banner */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/80 pb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-astro-primary" />
            <div>
              <h2 className="text-base sm:text-lg font-bold font-serif text-astro-primary">
                {isHi ? '१. वर्तमान स्थिति (Current Life State)' : '1. Vartamāna Sthiti: Current Life State'}
              </h2>
              <p className="text-[11px] text-astro-secondary-text">
                {isHi
                  ? `केंद्रित तिथि: ${currentState.focusDateFormatted} • यह स्थिति काल क्षितिज (Time Horizon) से पूर्णतः स्वतंत्र है`
                  : `Focus Date: ${currentState.focusDateFormatted} • Anchored to reference date; independent of Time Horizon`}
              </p>
            </div>
          </div>
          <span className="text-xs font-mono font-bold px-3 py-1 rounded-lg bg-astro-primary/10 text-astro-primary self-start sm:self-auto">
            {isHi ? 'सक्रिय अवस्था' : 'Active Present State'}
          </span>
        </div>

        {/* Current State Validity Window Card */}
        <div className="bg-astro-card border border-astro-border rounded-2xl p-4.5 space-y-3 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border/60 pb-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-astro-primary text-white tracking-wider flex items-center gap-1">
                  <Clock3 className="w-3 h-3 text-astro-accent" />
                  {isHi ? 'वर्तमान अवस्था वैधता कालखंड' : 'Current State Validity Window'}
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded border ${getLifecycleBadgeClass(validityWindow.currentPhase)}`}>
                  {validityWindow.currentPhase}
                </span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${getEvidenceStrengthBadge(snapshot.primaryActiveTheme.evidenceStrength)}`}>
                  {snapshot.primaryActiveTheme.evidenceStrength}
                </span>
              </div>

              <h3 className="font-bold text-astro-primary text-lg font-serif">
                {isHi ? snapshot.primaryActiveTheme.nameHi : snapshot.primaryActiveTheme.nameEn}
              </h3>
            </div>

            <div className="text-left sm:text-right bg-astro-bg sm:bg-transparent p-2.5 sm:p-0 rounded-xl border sm:border-0 border-astro-border/50 shrink-0">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">
                {isHi ? 'सक्रियता कालखंड (Activation Span)' : 'Astrological Activation Window'}
              </span>
              <span className="text-sm font-mono font-extrabold text-astro-primary block mt-0.5">
                {isHi ? validityWindow.durationLabelHi : validityWindow.durationLabelEn}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70 space-y-1">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">
                {isHi ? 'निर्धारक शास्त्रीय कारक' : 'Determining Astrological Factors'}
              </span>
              <p className="text-xs text-astro-text font-medium leading-tight">
                {isHi ? validityWindow.determiningFactorHi : validityWindow.determiningFactorEn}
              </p>
            </div>

            <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70 space-y-1">
              <span className="text-[10px] font-bold text-indigo-900 dark:text-indigo-300 uppercase block">
                {isHi ? 'तात्कालिक उत्प्रेरक (Immediate Trigger)' : 'Immediate Catalyst / Trigger'}
              </span>
              <p className="text-xs text-astro-text font-medium leading-tight">
                {isHi ? validityWindow.immediateTriggerWindowHi : validityWindow.immediateTriggerWindowEn}
              </p>
            </div>
          </div>
        </div>

        {/* 5-Tier Vimshottari Dasha Hierarchy Strip */}
        <div className="bg-white dark:bg-astro-card border border-astro-border rounded-2xl p-4 shadow-2xs space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-astro-primary" />
              <h3 className="font-bold text-astro-primary text-xs uppercase tracking-wider font-serif">
                {isHi ? '५-स्तरीय विंशोत्तरी दशा पदानुक्रम (Dasha Architecture at Focus Date)' : '5-Level Vimshottari Dasha Hierarchy (at Focus Date)'}
              </h3>
            </div>
            <span className="text-xs font-mono font-bold text-astro-text">
              {dasha5Level.activePathSummary}
            </span>
          </div>

          {/* 5-Tier Progress Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            {[dasha5Level.md, dasha5Level.ad, dasha5Level.pd, dasha5Level.sd, dasha5Level.prd].map((lvl) => (
              <div key={lvl.levelCode} className="p-2.5 bg-astro-bg rounded-xl border border-astro-border space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-astro-secondary-text uppercase">
                    {lvl.levelCode} • {lvl.levelName.split(' ')[0]}
                  </span>
                  <span className="font-bold text-astro-primary text-xs">
                    {lvl.planet}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-astro-border/60 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="bg-astro-primary h-full transition-all duration-300"
                    style={{ width: `${lvl.progressPercent}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[10px] text-astro-secondary-text font-mono">
                  <span>{lvl.progressPercent}%</span>
                  <span>{lvl.daysRemaining !== undefined ? `${lvl.daysRemaining}d left` : lvl.durationFormatted}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Dasha Sandhi Advisory Notification */}
          {dasha5Level.dashaSandhiNoteEn && (
            <div className="p-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-bold block text-[11px] uppercase tracking-wider">
                  {isHi ? 'दशा संधि परामर्श (Transition Zone)' : 'Dasha Sandhi Advisory'}
                </span>
                <p className="text-[11px] leading-relaxed">
                  {isHi ? dasha5Level.dashaSandhiNoteHi : dasha5Level.dashaSandhiNoteEn}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Authoritative Live Panchanga Context Strip */}
        {currentState.panchangaSummary && (
          <div className="bg-white dark:bg-astro-card border border-astro-border rounded-2xl p-4 shadow-2xs space-y-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-2">
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-astro-accent" />
                <h3 className="font-bold text-astro-primary text-xs uppercase tracking-wider font-serif">
                  {isHi ? 'तात्कालिक पंचांग एवं काल-तत्व (Panchanga Context at Focus Date)' : 'Live Panchanga & Cosmic Timing (at Focus Date)'}
                </h3>
              </div>
              <span className="text-[11px] font-mono text-astro-muted">
                {currentState.panchangaSummary.samvatString}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
              <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">{isHi ? 'तिथि' : 'Tithi'}</span>
                <span className="font-bold text-astro-text">{currentState.panchangaSummary.tithiName}</span>
                <span className="text-[10px] text-astro-muted block">{currentState.panchangaSummary.paksha}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">{isHi ? 'नक्षत्र' : 'Nakshatra'}</span>
                <span className="font-bold text-astro-text">{currentState.panchangaSummary.nakshatraName}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">{isHi ? 'योग / करण' : 'Yoga / Karana'}</span>
                <span className="font-bold text-astro-text">{currentState.panchangaSummary.yogaName}</span>
                <span className="text-[10px] text-astro-muted block">{currentState.panchangaSummary.karanaName}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-xl border border-astro-border/70">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">{isHi ? 'सक्रिय पर्व/व्रत' : 'Active Parva/Vrat'}</span>
                <span className="font-bold text-astro-accent">
                  {(isHi ? currentState.panchangaSummary.activeObservanceHi : currentState.panchangaSummary.activeObservance) || (isHi ? 'दैनिक नित्य दिनचर्या' : 'Daily Nitya Flow')}
                </span>
              </div>
            </div>

            <p className="text-xs text-astro-secondary-text p-2.5 bg-astro-bg/80 rounded-xl border border-astro-border/50">
              <span className="font-bold text-astro-primary">{isHi ? 'दैनिक संरेखण: ' : 'Daily Alignment: '}</span>
              {isHi ? currentState.panchangaSummary.dailyAlignmentHi : currentState.panchangaSummary.dailyAlignmentEn}
            </p>
          </div>
        )}

        {/* Vartamāna Anubhava (Current Lived Experience & Event Signatures) */}
        <div className="bg-astro-card border border-astro-border rounded-2xl p-5 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/70 pb-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-md bg-astro-primary text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-astro-accent" />
                  {isHi ? 'वर्तमान अनुभूति' : 'Vartamāna Anubhava'}
                </span>
                <h3 className="text-base font-bold font-serif text-astro-primary">
                  {isHi ? 'वर्तमान जीवन अनुभूति एवं घटना हस्ताक्षर' : 'Current Lived Experience & Event Signatures'}
                </h3>
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'अल्पकालिक मानसिक तरंगें, कार्यक्षेत्र में परिवर्तन, एवं आवासीय स्थानांतरण जैसे तात्कालिक प्रत्यक्ष अनुभवों का सूक्ष्म ज्योतिषीय संज्ञान।'
                  : 'Immediate lived experiences (e.g. daily emotional sensitivity, career transition, relocation prep) distinct from macro life themes.'}
              </p>
            </div>

            <span className="text-[11px] font-mono px-2.5 py-1 rounded-lg bg-astro-secondary-surface border border-astro-border text-astro-primary font-bold self-start sm:self-center">
              {currentLivedExperiences.length + convergenceSignatures.length} {isHi ? 'सक्रिय हस्ताक्षर' : 'Active Signatures'}
            </span>
          </div>

          {/* Convergence Signatures (Dual Work & Residence Pivot) */}
          {convergenceSignatures.length > 0 && (
            <div className="space-y-3">
              {convergenceSignatures.map((conv) => (
                <div
                  key={conv.signatureId}
                  className="bg-amber-500/10 border-2 border-amber-500/40 rounded-2xl p-4.5 space-y-3 shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-amber-500/30 pb-2.5">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center font-bold shadow-xs">
                        <Flame className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-amber-600 text-white shadow-2xs">
                            {isHi ? conv.statusLabelHi : conv.statusLabelEn}
                          </span>
                          <span className="text-xs font-mono font-bold text-amber-900 dark:text-amber-200">
                            {conv.startDate} → {conv.endDate}
                          </span>
                          <span className="text-[10px] text-amber-800/80 dark:text-amber-300 font-medium">
                            • {isHi ? conv.relativeTimingHi : conv.relativeTimingEn} ({conv.durationLabelEn})
                          </span>
                        </div>
                        <h4 className="font-bold text-astro-primary text-base font-serif mt-1">
                          {isHi ? conv.titleHi : conv.titleEn}
                        </h4>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full ${getEvidenceStrengthBadge(conv.evidenceStrength)}`}>
                        {conv.evidenceStrength}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-astro-text leading-relaxed font-medium">
                    {isHi ? conv.shortDescriptionHi : conv.shortDescriptionEn}
                  </p>

                  {/* Sthiti - Artha - Phala 3-Way Strip for Convergence */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs pt-1">
                    <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-amber-500/30 space-y-1">
                      <span className="text-[10px] font-bold text-astro-primary uppercase block">
                        {isHi ? '१. स्थिति (दोहरा अक्ष)' : '1. Sthiti (Dual Axis)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? conv.sthiti.astrologicalConditionHi : conv.sthiti.astrologicalConditionEn}
                      </p>
                    </div>

                    <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-amber-500/30 space-y-1">
                      <span className="text-[10px] font-bold text-amber-900 dark:text-amber-300 uppercase block">
                        {isHi ? '२. अर्थ (समन्वित पड़ाव)' : '2. Artha (Synthesized Pivot)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? conv.artha.meaningHi : conv.artha.meaningEn}
                      </p>
                    </div>

                    <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-amber-500/30 space-y-1">
                      <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-300 uppercase block">
                        {isHi ? '३. फल (मार्गदर्शन)' : '3. Phala (Guidance)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? conv.phala.manifestationHi : conv.phala.manifestationEn}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Current Lived Experience Signatures List */}
          {hasLivedExperiences ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {currentLivedExperiences.map((anubhava) => {
                const isExpanded = expandedAnubhavaId === anubhava.signatureId;

                return (
                  <div
                    key={anubhava.signatureId}
                    className={`bg-astro-card border rounded-2xl p-4 sm:p-5 space-y-3 shadow-2xs transition-all ${
                      anubhava.isImmediateToday
                        ? 'border-astro-accent/50 bg-astro-secondary-surface/40'
                        : 'border-astro-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 border-b border-astro-border/50 pb-2.5">
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-astro-secondary-surface border border-astro-border flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                          {getCategoryIcon(anubhava.category)}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider">
                              {isHi ? anubhava.categoryLabelHi : anubhava.categoryLabelEn}
                            </span>
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-astro-sage-tint text-astro-primary border border-astro-primary/30 font-semibold">
                              {anubhava.temporalTier}
                            </span>
                            {/* Authoritative Calendar Period Pill */}
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-astro-secondary-surface text-astro-primary border border-astro-border font-bold inline-flex items-center gap-1 shadow-2xs">
                              <Calendar className="w-3 h-3 text-astro-accent inline" />
                              {anubhava.startDate} → {anubhava.endDate}
                            </span>
                          </div>
                          <h4 className="font-bold text-astro-primary text-sm sm:text-base font-serif mt-1">
                            {isHi ? anubhava.titleHi : anubhava.titleEn}
                          </h4>
                        </div>
                      </div>

                      <span className={`text-[9px] px-2 py-0.5 rounded-full ${getEvidenceStrengthBadge(anubhava.evidenceStrength)} shrink-0`}>
                        {anubhava.evidenceStrength}
                      </span>
                    </div>

                    <p className="text-xs text-astro-text leading-relaxed">
                      {isHi ? anubhava.shortDescriptionHi : anubhava.shortDescriptionEn}
                    </p>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-[11px] pt-1.5 border-t border-astro-border/40 gap-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-astro-secondary-text font-medium flex items-center gap-1">
                          ⏱ {isHi ? anubhava.durationLabelHi : anubhava.durationLabelEn}
                        </span>
                        {anubhava.relativeTimingEn && (
                          <span className="text-astro-muted font-medium">
                            • {isHi ? anubhava.relativeTimingHi : anubhava.relativeTimingEn}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedAnubhavaId(isExpanded ? null : anubhava.signatureId)}
                        className="text-astro-primary hover:text-astro-accent font-bold flex items-center gap-1 cursor-pointer shrink-0 self-end sm:self-auto"
                      >
                        {isExpanded ? (isHi ? 'संक्षेप करें' : 'Less') : (isHi ? 'विस्तार देखें' : 'Details')}
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    </div>

                    {/* Expanded 3-Part Partition & Why Now? */}
                    {isExpanded && (
                      <div className="pt-2.5 border-t border-astro-border/60 space-y-2.5 animate-in fade-in duration-200 text-xs">
                        {/* Sthiti */}
                        <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                          <span className="text-[10px] font-bold text-astro-primary uppercase block">
                            {isHi ? '१. स्थिति (ग्रहीय विन्यास)' : '1. Sthiti (Astrological Configuration)'}
                          </span>
                          <p className="text-[11px] text-astro-text leading-tight">
                            {isHi ? anubhava.sthiti.astrologicalConditionHi : anubhava.sthiti.astrologicalConditionEn}
                          </p>
                        </div>

                        {/* Artha */}
                        <div className="p-2.5 bg-amber-50/40 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-900/60 space-y-1">
                          <span className="text-[10px] font-bold text-amber-900 dark:text-amber-300 uppercase block">
                            {isHi ? '२. अर्थ (शास्त्रीय तात्पर्य)' : '2. Artha (Classical Meaning)'}
                          </span>
                          <p className="text-[11px] text-astro-text leading-tight">
                            {isHi ? anubhava.artha.meaningHi : anubhava.artha.meaningEn}
                          </p>
                        </div>

                        {/* Phala */}
                        <div className="p-2.5 bg-emerald-50/40 dark:bg-emerald-950/20 rounded-lg border border-emerald-200 dark:border-emerald-900/60 space-y-1">
                          <span className="text-[10px] font-bold text-emerald-900 dark:text-emerald-300 uppercase block">
                            {isHi ? '३. फल (मार्गदर्शन)' : '3. Phala (Practical Response)'}
                          </span>
                          <p className="text-[11px] text-astro-text leading-tight">
                            {isHi ? anubhava.phala.manifestationHi : anubhava.phala.manifestationEn}
                          </p>
                          <ul className="text-[10px] text-emerald-950 dark:text-emerald-200 list-disc list-inside pt-1 space-y-0.5">
                            {(isHi ? anubhava.phala.actionableGuidanceHi : anubhava.phala.actionableGuidanceEn).map((g, idx) => (
                              <li key={idx}>{g}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-4 bg-astro-bg rounded-xl border border-astro-border text-center text-xs text-astro-secondary-text italic">
              {isHi ? noSignatureNoticeHi : noSignatureNoticeEn}
            </div>
          )}
        </div>

        {/* =================================================================== */}
        {/* CONCERN INTELLIGENCE: "WHAT'S ON YOUR MIND?" (GOV-066 STAGE 7)      */}
        {/* =================================================================== */}
        <WhatsOnYourMindSection
          kundali={kundali}
          focusDate={focusDateStr}
          isAcharyaView={isAcharyaView}
          lang={lang}
        />

        {/* Macro Life Themes (Vartamāna Jeevan Themes at Focus Date) */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-astro-primary" />
              <h3 className="font-bold text-astro-primary text-sm font-serif">
                {isHi ? 'दीर्घकालिक जीवन क्षेत्र: स्थिति, अर्थ एवं फल' : 'Macro Life Themes (Vartamāna Jeevan Themes): Sthiti, Artha & Phala'}
              </h3>
            </div>

            {/* Theme State Filters */}
            <div className="flex items-center gap-1 overflow-x-auto text-[11px]">
              <button
                type="button"
                onClick={() => setSelectedFilter('ALL')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  selectedFilter === 'ALL'
                    ? 'bg-astro-primary text-white border-astro-primary'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                {isHi ? `सभी (${activeThemes.length + emergingThemes.length})` : `All (${activeThemes.length + emergingThemes.length})`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedFilter('PEAK')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  selectedFilter === 'PEAK'
                    ? 'bg-astro-primary text-white border-astro-primary'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ★ {isHi ? `शिखर (${peakingThemes.length})` : `Peak (${peakingThemes.length})`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedFilter('ACTIVE')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  selectedFilter === 'ACTIVE'
                    ? 'bg-astro-primary text-white border-astro-primary'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ✓ {isHi ? `सक्रिय (${activeThemes.length})` : `Active (${activeThemes.length})`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedFilter('EMERGING')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  selectedFilter === 'EMERGING'
                    ? 'bg-astro-primary text-white border-astro-primary'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ▲ {isHi ? `उदीयमान (${emergingThemes.length})` : `Emerging (${emergingThemes.length})`}
              </button>
            </div>
          </div>

          {/* Theme Cards List */}
          <div className="space-y-4">
            {displayedThemes.map((theme) => {
              const isExpanded = expandedThemeId === theme.themeId;
              const deepDive = theme.acharyaDeepDive;

              return (
                <div
                  key={theme.themeId}
                  className="bg-white dark:bg-astro-card border border-astro-border rounded-2xl p-4.5 shadow-2xs space-y-3.5 transition-all"
                >
                  {/* Header Summary */}
                  <div
                    onClick={() => setExpandedThemeId(isExpanded ? null : theme.themeId)}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer select-none group border-b border-astro-border/50 pb-3"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-astro-primary text-base font-serif group-hover:text-astro-accent transition-colors">
                          {isHi ? theme.nameHi : theme.nameEn}
                        </h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${getLifecycleBadgeClass(theme.lifecycleState)}`}>
                          {theme.lifecycleState}
                        </span>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full ${getEvidenceStrengthBadge(theme.evidenceStrength)}`}>
                          {theme.evidenceStrength}
                        </span>
                      </div>

                      <p className="text-xs text-astro-secondary-text">
                        {isHi ? theme.sanskritName : `${theme.sanskritName} • Houses ${theme.relevantHouses.join(', ')} • Lord: ${theme.sthiti.bhaveshCondition}`}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-auto shrink-0">
                      <span className="text-[11px] text-astro-secondary-text font-bold sm:inline hidden">
                        {isExpanded ? (isHi ? 'प्रमाण संक्षेप करें' : 'Collapse Evidence') : (isHi ? 'विस्तृत प्रमाण देखें' : 'Examine Full Evidence')}
                      </span>
                      <button
                        type="button"
                        className="p-1.5 rounded-lg hover:bg-astro-border text-astro-primary transition-colors cursor-pointer"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* 3-Part Separation: Sthiti vs Artha vs Phala */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    {/* 1. Sthiti */}
                    <div className="p-3 bg-astro-bg rounded-xl border border-astro-border space-y-1.5">
                      <span className="text-[10px] font-bold text-astro-primary uppercase tracking-wider block flex items-center gap-1">
                        <Compass className="w-3 h-3 text-astro-accent" />
                        {isHi ? '१. स्थिति (ग्रहीय विन्यास)' : '1. Sthiti (Astrological Mechanics)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? theme.sthiti.astrologicalConditionHi : theme.sthiti.astrologicalConditionEn}
                      </p>
                      <div className="text-[10px] text-astro-secondary-text pt-1 border-t border-astro-border/50">
                        <strong>Gochar Trigger:</strong> {isHi ? theme.sthiti.gocharInfluenceHi : theme.sthiti.gocharInfluenceEn}
                      </div>
                    </div>

                    {/* 2. Artha */}
                    <div className="p-3 bg-amber-50/40 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-900/60 space-y-1.5">
                      <span className="text-[10px] font-bold text-amber-900 dark:text-amber-300 uppercase tracking-wider block flex items-center gap-1">
                        <BookOpen className="w-3 h-3 text-amber-700" />
                        {isHi ? '२. अर्थ (ज्योतिषीय तात्पर्य)' : '2. Artha (Jyotish Significance)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? theme.artha.meaningHi : theme.artha.meaningEn}
                      </p>
                      <div className="text-[10px] text-amber-950 dark:text-amber-200 pt-1 border-t border-amber-200/60 dark:border-amber-800/60">
                        <strong>Karmic Domain:</strong> {isHi ? theme.artha.karmicDomainNoteHi : theme.artha.karmicDomainNoteEn}
                      </div>
                    </div>

                    {/* 3. Phala */}
                    <div className="p-3 bg-emerald-50/40 dark:bg-emerald-950/20 rounded-xl border border-emerald-200 dark:border-emerald-900/60 space-y-1.5">
                      <span className="text-[10px] font-bold text-emerald-900 dark:text-emerald-300 uppercase tracking-wider block flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-emerald-700" />
                        {isHi ? '३. फल (संभावित अभिव्यक्ति)' : '3. Phala (Potential Manifestation)'}
                      </span>
                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? theme.phala.potentialManifestationHi : theme.phala.potentialManifestationEn}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-emerald-200/60 dark:border-emerald-800/60">
                        <span className="text-[10px] font-bold text-emerald-950 dark:text-emerald-300">{isHi ? 'अनुकूल:' : 'Avenues:'}</span>
                        {(isHi ? theme.phala.growthAvenuesHi : theme.phala.growthAvenuesEn).slice(0, 2).map((kw, i) => (
                          <span key={i} className="text-[9px] px-1.5 py-0.2 rounded bg-emerald-100 dark:bg-emerald-900/50 text-emerald-950 dark:text-emerald-200 font-medium">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Evidence Chain */}
                  {isExpanded && (
                    <div className="pt-3 border-t border-astro-border/60 space-y-3.5 animate-in fade-in duration-200">
                      {/* Standard Hierarchical Evidence Chain */}
                      <div className="space-y-1.5">
                        <span className="text-[11px] font-bold text-astro-primary uppercase tracking-wider block">
                          {isHi ? 'शास्त्रीय प्रमाण श्रृंखला (Evidence Hierarchy):' : 'Why Is This Active Now? (Evidence Hierarchy)'}
                        </span>
                        {theme.evidenceChain.map((step, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2.5 p-2 rounded-lg bg-astro-bg border border-astro-border/50 text-xs"
                          >
                            <span className="w-5 h-5 rounded-full bg-astro-primary text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                              {idx + 1}
                            </span>
                            <div className="space-y-0.5 flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-astro-primary text-[11px]">
                                  {step.hierarchyTier} • {step.factor}
                                </span>
                                <span className="text-[10px] font-mono text-astro-secondary-text">
                                  Weight: {step.supportWeight}%
                                </span>
                              </div>
                              <p className="text-[11px] text-astro-text leading-tight">
                                {isHi ? step.descriptionHi : step.descriptionEn}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Supporting vs Contradictory factors */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
                        <div className="p-2.5 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-xl space-y-1">
                          <span className="text-[10px] font-bold text-emerald-900 dark:text-emerald-300 uppercase block">
                            ✓ {isHi ? 'समर्थक शास्त्रीय प्रमाण' : 'Supporting Evidence'}
                          </span>
                          <ul className="text-[11px] space-y-0.5 text-emerald-950 dark:text-emerald-200 list-disc list-inside">
                            {theme.supportingFactors.map((sf, i) => (
                              <li key={i}>{sf}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="p-2.5 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl space-y-1">
                          <span className="text-[10px] font-bold text-amber-900 dark:text-amber-300 uppercase block">
                            ▲ {isHi ? 'सीमाएं एवं परस्पर विरोधी कारक' : 'Limiting & Contradictory Nuances'}
                          </span>
                          {theme.limitingContradictoryFactors.length > 0 ? (
                            <ul className="text-[11px] space-y-0.5 text-amber-950 dark:text-amber-200 list-disc list-inside">
                              {theme.limitingContradictoryFactors.map((lf, i) => (
                                <li key={i}>{lf}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-[11px] text-astro-secondary-text italic">
                              {isHi ? 'कोई गंभीर बाधा उपस्थित नहीं' : 'No acute counteracting factors detected.'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 4. TIME HORIZON SECTION (SEPARATE TEMPORAL EVOLUTION LAYER)          */}
      {/* Mandatory: Appears AFTER Vartamāna Sthiti                           */}
      {/* =================================================================== */}
      <div className="bg-astro-card border-2 border-astro-primary/30 rounded-2xl p-5 space-y-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border/70 pb-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-astro-accent" />
              <h2 className="text-base font-bold font-serif text-astro-primary">
                {isHi ? '२. काल क्षितिज (Time Horizon): गतिशील कालिक विकास' : '2. Time Horizon: Dynamic Temporal Evolution'}
              </h2>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'यह क्षितिज संचयी रूप से (Day 0 → Day X) आगामी दशा, गोचर व जीवन-पड़ावों के प्रवाह का विश्लेषण करता है। वर्तमान स्थिति अपरिवर्तित रहती है।'
                : 'Examines future transitions cumulatively from Day 0 to Day X. The top Vartamāna Sthiti remains anchored to your Focus Date.'}
            </p>
          </div>

          <div className="flex items-center gap-1.5 self-start sm:self-auto">
            <span className="text-[10px] text-astro-secondary-text font-mono font-bold">
              {horizonAnalysis.intervalLabelEn}
            </span>
          </div>
        </div>

        {/* User-Selectable Calendar Product Horizons */}
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
            {isHi ? 'विश्लेषण कालिक क्षितिज चुनें (Select Analysis Horizon):' : 'Select Analysis Horizon:'}
          </span>
          <div className="flex items-center gap-2 overflow-x-auto custom-horizontal-scrollbar pb-1">
            {[
              { id: '7D' as VartamanaHorizon, labelEn: '7 Days', labelHi: '७ दिन' },
              { id: '30D' as VartamanaHorizon, labelEn: '30 Days', labelHi: '३० दिन' },
              { id: '90D' as VartamanaHorizon, labelEn: '90 Days (Quarterly)', labelHi: '९० दिन (त्रैमासिक)' },
              { id: '1Y' as VartamanaHorizon, labelEn: '1 Year (Annual)', labelHi: '१ वर्ष (वार्षिक)' },
              { id: '3Y' as VartamanaHorizon, labelEn: '3 Years (Medium-Term)', labelHi: '३ वर्ष (मध्यम-कालिक)' },
              { id: '10Y' as VartamanaHorizon, labelEn: '10 Years (Decadal Macro)', labelHi: '१० वर्ष (दशकीय दीर्घ)' }
            ].map(hz => (
              <button
                key={hz.id}
                type="button"
                onClick={() => setSelectedHorizon(hz.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 ${
                  selectedHorizon === hz.id
                    ? 'bg-astro-primary text-white shadow-xs scale-102'
                    : 'bg-white dark:bg-astro-bg text-astro-secondary-text border border-astro-border hover:text-astro-primary'
                }`}
              >
                <span>{isHi ? hz.labelHi : hz.labelEn}</span>
                {selectedHorizon === hz.id && <CheckCircle2 className="w-3.5 h-3.5 text-astro-accent" />}
              </button>
            ))}
          </div>
        </div>

        {/* Period-Driven Astrological Analytical Boundary Selector */}
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
            {isHi ? 'दशा-गोचर चक्र सीमा चुनें (Select Astrological Period Boundary):' : 'Select Astrological Period Boundary:'}
          </span>
          <div className="flex items-center gap-2 overflow-x-auto custom-horizontal-scrollbar pb-1">
            {[
              { id: 'current_pd' as VartamanaHorizon, labelEn: 'Active PD Window', labelHi: 'सक्रिय प्रत्यंतर काल' },
              { id: 'current_ad' as VartamanaHorizon, labelEn: 'Active Antardasha (Bhukti)', labelHi: 'सक्रिय अंतर्दशा काल' },
              { id: 'ad_transition' as VartamanaHorizon, labelEn: 'Next Antardasha Transition', labelHi: 'आगामी अंतर्दशा संक्रान्ति' },
              { id: 'double_transit' as VartamanaHorizon, labelEn: 'Jupiter–Saturn Gochar Cycle', labelHi: 'गुरु-शनि गोचर चक्र' },
              { id: 'mahadasha_arc' as VartamanaHorizon, labelEn: 'Full Mahadasha Arc', labelHi: 'संपूर्ण महादशा महाचक्र' }
            ].map(hz => (
              <button
                key={hz.id}
                type="button"
                onClick={() => setSelectedHorizon(hz.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 ${
                  selectedHorizon === hz.id
                    ? 'bg-astro-primary text-white shadow-xs scale-102'
                    : 'bg-white dark:bg-astro-bg text-astro-secondary-text border border-astro-border hover:text-astro-primary'
                }`}
              >
                <span>{isHi ? hz.labelHi : hz.labelEn}</span>
                {selectedHorizon === hz.id && <CheckCircle2 className="w-3.5 h-3.5 text-astro-accent" />}
              </button>
            ))}
          </div>
        </div>

        {/* Horizon Overview Banner */}
        <div className="p-3.5 bg-astro-bg rounded-xl border border-astro-border/70 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <strong className="text-astro-primary">{isHi ? 'क्षितिज विश्लेषण सारांश:' : 'Horizon Evolution Overview:'}</strong>
            <span className="font-mono text-astro-secondary-text text-[11px]">
              {horizonAnalysis.transitionsList.length} {isHi ? 'संक्रमण पड़ाव' : 'Key Transitions'}
            </span>
          </div>
          <p className="text-xs text-astro-text leading-relaxed">
            {isHi ? horizonAnalysis.horizonSummaryHi : horizonAnalysis.horizonSummaryEn}
          </p>
        </div>

        {/* What Is Changing & What Comes Next Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 text-xs">
          {/* What Is Changing */}
          <div className="p-3.5 bg-white dark:bg-astro-bg rounded-xl border border-astro-border space-y-2">
            <span className="text-[10px] font-bold text-astro-primary uppercase tracking-wider block flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? 'इस कालखंड में क्या परिवर्तित हो रहा है?' : 'What Is Changing Across This Period?'}
            </span>
            <ul className="space-y-1.5 text-astro-text leading-relaxed">
              {(isHi ? horizonAnalysis.whatIsChangingHi : horizonAnalysis.whatIsChangingEn).map((item, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <span className="text-astro-primary font-bold mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What Comes Next */}
          <div className="p-3.5 bg-white dark:bg-astro-bg rounded-xl border border-astro-border space-y-2">
            <span className="text-[10px] font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider block flex items-center gap-1.5">
              <ArrowRight className="w-3.5 h-3.5 text-indigo-600" />
              {isHi ? 'आगामी पड़ाव एवं दिशा (What Comes Next?)' : 'Downstream Trajectory: What Comes Next?'}
            </span>
            <ul className="space-y-1.5 text-astro-text leading-relaxed">
              {(isHi ? horizonAnalysis.whatComesNextHi : horizonAnalysis.whatComesNextEn).map((item, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <span className="text-indigo-600 font-bold mt-0.5">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* No Transition Notice if empty */}
        {horizonAnalysis.noTransitionNoticeEn && (
          <div className="p-3 bg-astro-secondary-surface rounded-xl border border-astro-border text-xs text-astro-secondary-text text-center italic">
            {isHi ? horizonAnalysis.noTransitionNoticeHi : horizonAnalysis.noTransitionNoticeEn}
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* 5. DYNAMIC JYOTISH SAMAYA REKHA (TEMPORAL TIMELINE)                 */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-astro-border/70 pb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-astro-primary" />
            <h3 className="font-bold text-astro-primary text-sm font-serif">
              {isHi ? 'ज्योतिष समय रेखा: अतीत ← वर्तमान → आगामी' : 'Dynamic Jyotish Samaya Rekha: Past ← Present → Future'}
            </h3>
          </div>
          <span className="text-[10px] text-astro-secondary-text font-mono">
            {timelineMarkers.length} Key Temporal Markers ({selectedHorizon})
          </span>
        </div>

        {/* Timeline Strip */}
        <div className="space-y-2.5">
          {timelineMarkers.map((marker) => (
            <div
              key={marker.id}
              className={`p-3 rounded-xl border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all ${
                marker.isCurrent
                  ? 'bg-astro-primary/10 border-astro-primary/40 font-bold'
                  : marker.isPast
                  ? 'bg-white/40 dark:bg-astro-bg/40 border-astro-border/60 opacity-80'
                  : 'bg-white dark:bg-astro-card border-astro-border shadow-2xs'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs shrink-0 font-bold ${
                  marker.isCurrent
                    ? 'bg-astro-primary text-white shadow-xs'
                    : marker.isPast
                    ? 'bg-astro-secondary-surface text-astro-secondary'
                    : 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                }`}>
                  {marker.markerType === 'Dasha_Parivartana' ? 'DP' : marker.markerType === 'Dasha_Sandhi' ? 'DS' : marker.markerType === 'Gochar_Pravesha' ? 'GP' : 'GT'}
                </div>

                <div className="space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-astro-primary text-xs">
                      {isHi ? marker.titleHi : marker.titleEn}
                    </span>
                    <span className="text-[10px] px-2 py-0.2 rounded bg-astro-card border border-astro-border text-astro-secondary-text font-mono">
                      {marker.relativeTimeTagEn}
                    </span>
                  </div>
                  <p className="text-[11px] text-astro-text font-normal leading-tight">
                    {isHi ? marker.descriptionHi : marker.descriptionEn}
                  </p>
                </div>
              </div>

              <div className="text-right shrink-0">
                <span className="text-xs font-mono font-bold text-astro-secondary-text block">
                  {formatDate(marker.date)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =================================================================== */}
      {/* 6. ACHARYA DEEP DIVE (TECHNICAL EVIDENCE DOSSIER)                   */}
      {/* =================================================================== */}
      {isAcharyaView && (
        <div className="bg-white dark:bg-astro-card border-2 border-astro-primary/30 rounded-2xl p-5 space-y-4 shadow-sm animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-astro-border pb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-astro-primary" />
              <h3 className="font-bold text-astro-primary text-sm font-serif">
                {isHi ? 'आचार्य तकनीकी विश्लेषण एवं प्रामाणिकता पटल' : 'Acharya Technical Evidence Dossier & Provenance'}
              </h3>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 font-mono font-bold">
              Gateway V2 Verified
            </span>
          </div>

          {/* Acharya Tab Switcher */}
          <div className="flex items-center gap-1.5 text-xs overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setActiveAcharyaTab('D1_VARGA')}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                activeAcharyaTab === 'D1_VARGA'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'bg-astro-bg text-astro-secondary-text border border-astro-border'
              }`}
            >
              D1 & Vargas Mechanics
            </button>
            <button
              type="button"
              onClick={() => setActiveAcharyaTab('DASHA_SANDHI')}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                activeAcharyaTab === 'DASHA_SANDHI'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'bg-astro-bg text-astro-secondary-text border border-astro-border'
              }`}
            >
              Dasha Bounds & Sandhi
            </button>
            <button
              type="button"
              onClick={() => setActiveAcharyaTab('GOCHAR_TRIGGERS')}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                activeAcharyaTab === 'GOCHAR_TRIGGERS'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'bg-astro-bg text-astro-secondary-text border border-astro-border'
              }`}
            >
              Gochar & Precision Triggers
            </button>
            <button
              type="button"
              onClick={() => setActiveAcharyaTab('REASONING_CHAIN')}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                activeAcharyaTab === 'REASONING_CHAIN'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'bg-astro-bg text-astro-secondary-text border border-astro-border'
              }`}
            >
              4-Step Reasoning Chain
            </button>
          </div>

          {/* Tab Panes */}
          {activeAcharyaTab === 'D1_VARGA' && activeThemes[0]?.acharyaDeepDive && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 text-xs pt-1">
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Lagna & Lagnesh</span>
                <p className="font-bold text-astro-primary">{activeThemes[0].acharyaDeepDive.d1Mechanics.lagna} ({activeThemes[0].acharyaDeepDive.d1Mechanics.lagnaLord})</p>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Bhavesh Placement</span>
                <p className="font-bold text-astro-text">{activeThemes[0].acharyaDeepDive.d1Mechanics.bhavesh} in H{activeThemes[0].acharyaDeepDive.d1Mechanics.bhaveshHouse} ({activeThemes[0].acharyaDeepDive.d1Mechanics.bhaveshSign})</p>
                <span className="text-[10px] text-astro-secondary-text">{activeThemes[0].acharyaDeepDive.d1Mechanics.bhaveshDegree} • {activeThemes[0].acharyaDeepDive.d1Mechanics.bhaveshNakshatra}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Dignity & Yogas</span>
                <p className="font-bold text-astro-text">{activeThemes[0].acharyaDeepDive.d1Mechanics.dignity}</p>
                <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium">{activeThemes[0].acharyaDeepDive.d1Mechanics.relevantYoga}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Corroboration</span>
                <p className="font-bold text-astro-text">D9: {activeThemes[0].acharyaDeepDive.corroborationDetails.d9Sign} ({activeThemes[0].acharyaDeepDive.corroborationDetails.d9Dignity})</p>
                <span className="text-[10px] text-astro-secondary-text font-mono">Shadbala: {activeThemes[0].acharyaDeepDive.corroborationDetails.shadbalaRatio}x • SAV: {activeThemes[0].acharyaDeepDive.corroborationDetails.ashtakavargaSAV}</span>
              </div>
            </div>
          )}

          {activeAcharyaTab === 'DASHA_SANDHI' && activeThemes[0]?.acharyaDeepDive && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs pt-1">
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1 font-mono">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">5-Level Exact Boundaries</span>
                <div className="text-[11px] space-y-0.5">
                  <div><strong>MD ({activeThemes[0].acharyaDeepDive.dashaSanction.mdPlanet}):</strong> {activeThemes[0].acharyaDeepDive.dashaSanction.mdPeriod}</div>
                  <div><strong>AD ({activeThemes[0].acharyaDeepDive.dashaSanction.adPlanet}):</strong> {activeThemes[0].acharyaDeepDive.dashaSanction.adPeriod}</div>
                  <div><strong>PD ({activeThemes[0].acharyaDeepDive.dashaSanction.pdPlanet}):</strong> {activeThemes[0].acharyaDeepDive.dashaSanction.pdPeriod}</div>
                  <div><strong>SD ({activeThemes[0].acharyaDeepDive.dashaSanction.sdPlanet}):</strong> {activeThemes[0].acharyaDeepDive.dashaSanction.sdPeriod}</div>
                  <div><strong>PRD ({activeThemes[0].acharyaDeepDive.dashaSanction.prdPlanet}):</strong> {activeThemes[0].acharyaDeepDive.dashaSanction.prdPeriod}</div>
                </div>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Sandhi Evaluation</span>
                <p className="text-xs text-astro-text">{activeThemes[0].acharyaDeepDive.dashaSanction.dashaSandhiStatus}</p>
                <div className="pt-2 text-[10px] text-astro-secondary-text border-t border-astro-border/50">
                  <strong>Jaimini Karaka Alignment:</strong> {activeThemes[0].acharyaDeepDive.corroborationDetails.jaiminiKarakaRole || 'N/A'}
                </div>
              </div>
            </div>
          )}

          {activeAcharyaTab === 'GOCHAR_TRIGGERS' && activeThemes[0]?.acharyaDeepDive && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs pt-1">
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Transiting Planet</span>
                <p className="font-bold text-astro-primary">{activeThemes[0].acharyaDeepDive.gocharPrecision.transitingPlanet} in H{activeThemes[0].acharyaDeepDive.gocharPrecision.transitingBhava} ({activeThemes[0].acharyaDeepDive.gocharPrecision.transitingSign})</p>
                <span className="text-[10px] font-mono text-astro-secondary-text">{activeThemes[0].acharyaDeepDive.gocharPrecision.transitingDegree} {activeThemes[0].acharyaDeepDive.gocharPrecision.isRetrograde ? '(Vakri)' : '(Margi)'}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-text uppercase">Contact & Aspect Angle</span>
                <p className="font-bold text-astro-text">{activeThemes[0].acharyaDeepDive.gocharPrecision.aspectOrYuti}</p>
                <span className="text-[10px] text-astro-secondary-text">{activeThemes[0].acharyaDeepDive.gocharPrecision.orbExactness}</span>
              </div>
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Orb Phase</span>
                <p className="font-bold text-astro-accent">{activeThemes[0].acharyaDeepDive.gocharPrecision.phase}</p>
                <span className="text-[10px] text-astro-secondary-text">BAV Points: {activeThemes[0].acharyaDeepDive.corroborationDetails.ashtakavargaBAV}/8</span>
              </div>
            </div>
          )}

          {activeAcharyaTab === 'REASONING_CHAIN' && activeThemes[0]?.acharyaDeepDive && (
            <div className="space-y-1.5 pt-1 text-xs">
              <div className="p-2.5 rounded-lg bg-astro-bg border border-astro-border/70 space-y-0.5">
                <span className="font-bold text-[10px] text-astro-primary uppercase block">1. Natal Promise Foundation</span>
                <p className="text-[11px] text-astro-text leading-tight">{activeThemes[0].acharyaDeepDive.reasoningChain.step1NatalPromise}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-astro-bg border border-astro-border/70 space-y-0.5">
                <span className="font-bold text-[10px] text-astro-primary uppercase block">2. Dasha Sanction Tier</span>
                <p className="text-[11px] text-astro-text leading-tight">{activeThemes[0].acharyaDeepDive.reasoningChain.step2DashaSanction}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-astro-bg border border-astro-border/70 space-y-0.5">
                <span className="font-bold text-[10px] text-astro-primary uppercase block">3. Gochar Catalyst Trigger</span>
                <p className="text-[11px] text-astro-text leading-tight">{activeThemes[0].acharyaDeepDive.reasoningChain.step3GocharTrigger}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-astro-bg border border-astro-border/70 space-y-0.5">
                <span className="font-bold text-[10px] text-emerald-800 dark:text-emerald-300 uppercase block">4. Synthesis & Verdict</span>
                <p className="text-[11px] text-astro-text leading-tight font-medium">{activeThemes[0].acharyaDeepDive.reasoningChain.step4Synthesis}</p>
              </div>
            </div>
          )}

          {/* Audit Provenance & BTR Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs pt-2 border-t border-astro-border">
            <div className="p-3 bg-astro-bg rounded-xl border border-astro-border space-y-1.5">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">
                Single Source of Truth Provenance
              </span>
              <p className="text-xs font-mono text-astro-text leading-relaxed">
                {acharyaGatewayStatus.calculationProvenance}
              </p>
              <div className="text-[10px] text-emerald-800 dark:text-emerald-300 font-medium pt-1 border-t border-astro-border/50">
                {acharyaGatewayStatus.safetyComplianceNotice}
              </div>
            </div>

            <div className="p-3 bg-astro-bg rounded-xl border border-astro-border space-y-1.5">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">
                Birth Time Rectification (BTR) Baseline
              </span>
              <div className="flex justify-between items-center text-xs">
                <span className="text-astro-secondary-text">Working Birth Time:</span>
                <span className="font-mono font-bold text-astro-primary">{btrStatus.workingTime}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-astro-secondary-text">Confidence Status:</span>
                <span className="font-semibold text-astro-text">{btrStatus.rectificationConfidence}</span>
              </div>
              {btrStatus.uncertaintyNotice && (
                <p className="text-[10px] text-amber-700 dark:text-amber-300 italic pt-1">
                  * {btrStatus.uncertaintyNotice}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
